import type { Order } from "@graphql/generated/types";
import type { IAdyenPaymentMethodStrategy } from "@interfaces/payment-method/AdyenPaymentMethodStrategy";
import type { PaymentValues } from "@lib/utils/checkoutHelper";
import { EcontextPaymentMethod } from "@lib/constants/payment";
import { getReadablePaymentMethod, logError } from "./adyenPaymentMethodUtils";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

export class AdyenDeferredPaymentStrategy implements IAdyenPaymentMethodStrategy {
    private readonly convStorePayment = EcontextPaymentMethod.Stores;
    private readonly emptyPaymentValues: PaymentValues = {
        paymentSystem: null,
        paymentSystemIcon: null,
        paymentReference: null,
        paymentDateOfExpiry: null,
    };

    resolve(order: Order): PaymentValues {
        if (this.hasMultiplePayments(order)) {
            this.logMultiplePayments(order);
            return this.emptyPaymentValues;
        }

        const payment = this.findConvenienceStorePayment(order);

        if (!payment) {
            logError("PAYMENT: Convenience Stores payment not found", order?.cart?.id);
            return this.emptyPaymentValues;
        }

        const paymentSystemIcon = this.getPaymentSystemIcon(payment);
        const customFieldMakePaymentResponse = this.getCustomFieldMakePaymentResponse(payment);

        const paymentReference = customFieldMakePaymentResponse?.action?.reference
            ? `Payment Reference: ${customFieldMakePaymentResponse?.action?.reference}`
            : null;

        if (!paymentReference) {
            logError("PAYMENT: Payment reference not found", order?.cart?.id);
        }

        const paymentDateOfExpiry = customFieldMakePaymentResponse?.action?.expiresAt
            ? `Date of Expiry: ${customFieldMakePaymentResponse?.action?.expiresAt}`
            : null;

        if (!paymentDateOfExpiry) {
            logError("PAYMENT: Payment date of expiry not found", order?.cart?.id);
        }

        return {
            paymentSystem: getReadablePaymentMethod(payment.paymentMethodInfo.method),
            paymentSystemIcon,
            paymentReference: paymentReference,
            paymentDateOfExpiry: paymentDateOfExpiry,
        };
    }

    // We should have only one payment for deferred payment when using advanced flow
    private hasMultiplePayments(order: Order): boolean {
        return order?.paymentInfo?.payments?.length > 1;
    }

    private logMultiplePayments(order: Order): void {
        const id = order?.cart?.id;
        Logger.info(
            ServiceType.ADYEN,
            `PAYMENT: Deferred payment has more than one payment for cartId: ${id}`,
            {
                tag: LogTag.PAYMENT,
                cartId: id,
            }
        );
    }

    private findConvenienceStorePayment(order: Order) {
        return (
            order?.paymentInfo?.payments
                ?.filter((payment) => payment?.paymentMethodInfo.method === this.convStorePayment)
                ?.reverse()
                ?.find((payment) => payment) || null
        );
    }

    private getPaymentSystemIcon(payment): string | null {
        return payment?.paymentMethodInfo?.method === this.convStorePayment
            ? "/assets/icons/konbini-logo.svg"
            : null;
    }

    private getCustomFieldMakePaymentResponse(payment) {
        const customFiledmakePaymentResponse = payment?.custom?.customFieldsRaw?.find(
            (customField) => customField?.name === "makePaymentResponse"
        );

        return customFiledmakePaymentResponse
            ? JSON.parse(customFiledmakePaymentResponse?.value)
            : null;
    }
}
